import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Info as InfoIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Star as AmenitiesIcon,
  AttachMoney as PricingIcon,
  Rule as RulesIcon,
  PhotoLibrary as PhotosIcon,
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  Pause as PauseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { ListingForm as ListingFormType, Listing } from '@/types';
import { BasicInfoTab } from './tabs/BasicInfoTab';
import { LocationTab } from './tabs/LocationTab';
import { CapacityTab } from './tabs/CapacityTab';
import { AmenitiesTab } from './tabs/AmenitiesTab';
import { PricingTab } from './tabs/PricingTab';
import { RulesPoliciesTab } from './tabs/RulesPoliciesTab';
import { PhotosTab } from './tabs/PhotosTab';
import { ListingsService } from '@/utils/listings-service';
import { ListingFormValidator } from '@/utils/listing-form-validation';

interface ListingFormTabsProps {
  listing?: Listing;
  onSave: (listingData: ListingFormType) => Promise<void>;
  onPublish?: (listingId: string) => Promise<void>;
  onPause?: (listingId: string) => Promise<void>;
  isLoading?: boolean;
  initialTab?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`listing-tabpanel-${index}`}
      aria-labelledby={`listing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}



const TABS = [
  { label: 'Basic Info', icon: InfoIcon },
  { label: 'Location', icon: LocationIcon },
  { label: 'Capacity', icon: PeopleIcon },
  { label: 'Amenities', icon: AmenitiesIcon },
  { label: 'Pricing', icon: PricingIcon },
  { label: 'Rules & Policies', icon: RulesIcon },
  { label: 'Photos', icon: PhotosIcon },
];

export const ListingFormTabs: React.FC<ListingFormTabsProps> = ({
  listing,
  onSave,
  onPublish,
  onPause,
  isLoading = false,
  initialTab = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ListingFormType>({
    title: '',
    description: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
    },
    amenities: [],
    pricing: {
      basePrice: 0,
      currency: 'USD',
      cleaningFee: 0,
    },
    photos: [],
    maxGuests: 1,
    bedrooms: 1,
    bathrooms: 1,
    beds: 1,
    propertyType: '',
    roomType: '',
    checkInTime: '15:00',
    checkOutTime: '11:00',
    minimumStay: 1,
    maximumStay: 365,
    instantBook: false,
    houseRules: [],
    cancellationPolicy: 'moderate',
    accessibility: {
      wheelchairAccessible: false,
      stepFreeAccess: false,
      accessibleParking: false,
      accessibleBathroom: false,
    },
    safetyFeatures: [],
    additionalInfo: {
      petPolicy: 'not_allowed',
      smokingPolicy: 'not_allowed',
      partiesAllowed: false,
      additionalRules: [],
    },
  });

  // Initialize form with existing listing data
  useEffect(() => {
    if (listing && listing.listingId) {
      setFormData({
        title: listing.title || '',
        description: listing.description || '',
        address: listing.address || {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'United States',
        },
        amenities: listing.amenities || [],
        pricing: listing.pricing || {
          basePrice: 0,
          currency: 'USD',
          cleaningFee: 0,
        },
        photos: listing.photos || [],
        maxGuests: listing.maxGuests || 1,
        bedrooms: listing.bedrooms || 1,
        bathrooms: listing.bathrooms || 1,
        beds: listing.beds || 1,
        propertyType: listing.propertyType || '',
        roomType: listing.roomType || '',
        checkInTime: listing.checkInTime || '15:00',
        checkOutTime: listing.checkOutTime || '11:00',
        minimumStay: listing.minimumStay || 1,
        maximumStay: listing.maximumStay || 365,
        instantBook: listing.instantBook || false,
        houseRules: listing.houseRules || [],
        cancellationPolicy: listing.cancellationPolicy || 'moderate',
        accessibility: listing.accessibility || {
          wheelchairAccessible: false,
          stepFreeAccess: false,
          accessibleParking: false,
          accessibleBathroom: false,
        },
        safetyFeatures: listing.safetyFeatures || [],
        additionalInfo: listing.additionalInfo || {
          petPolicy: 'not_allowed',
          smokingPolicy: 'not_allowed',
          partiesAllowed: false,
          additionalRules: [],
        },
      });
    }
  }, [listing]); // Depend on the entire listing object to ensure we update when data changes

  const completionStatus = ListingsService.checkListingCompleteness(formData);
  const enhancedCompletion = ListingFormValidator.getOverallCompletion(formData);
  const validationResult = ListingFormValidator.validateFullForm(formData);
  
  // Merge completion data for display
  const displayCompletionStatus = {
    ...completionStatus,
    completionPercentage: enhancedCompletion.completionPercentage,
    isComplete: enhancedCompletion.isComplete,
  };



  const handleFormDataChange = (updates: Partial<ListingFormType>) => {
    setFormData(prev => ({
      ...prev,
      ...updates,
    }));
    
    // Clear related errors
    const updatedFields = Object.keys(updates);
    const clearedErrors = { ...errors };
    updatedFields.forEach(field => {
      if (clearedErrors[field]) {
        delete clearedErrors[field];
      }
    });
    setErrors(clearedErrors);
  };

  const handleErrorsChange = (newErrors: Record<string, string>) => {
    setErrors(prev => ({
      ...prev,
      ...newErrors,
    }));
  };

  const clearErrors = (fields: string[]) => {
    const clearedErrors = { ...errors };
    fields.forEach(field => {
      delete clearedErrors[field];
    });
    setErrors(clearedErrors);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Failed to save listing:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!listing?.listingId || !displayCompletionStatus.isComplete) return;
    
    setIsSubmitting(true);
    try {
      await onPublish?.(listing.listingId);
    } catch (error) {
      console.error('Failed to publish listing:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePauseToggle = async () => {
    if (!listing?.listingId) return;
    
    setIsSubmitting(true);
    try {
      await onPause?.(listing.listingId);
    } catch (error) {
      console.error('Failed to update listing status:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to check if a step is completed using detailed validation
  const isStepCompleted = (stepIndex: number) => {
    const validationMethods = [
      ListingFormValidator.validateBasicInfo,
      ListingFormValidator.validateLocation,
      ListingFormValidator.validateCapacity,
      ListingFormValidator.validateAmenities,
      ListingFormValidator.validatePricing,
      ListingFormValidator.validateRulesAndPolicies,
      ListingFormValidator.validatePhotos,
    ];
    
    if (stepIndex < validationMethods.length) {
      const validation = validationMethods[stepIndex](formData);
      return validation.isComplete;
    }
    
    return false;
  };

  return (
    <Box>
      {/* Progress Bar and Steps Navigation */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Listing Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayCompletionStatus.completionPercentage}% Complete
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={displayCompletionStatus.completionPercentage}
            color={displayCompletionStatus.completionPercentage >= 100 ? 'success' : 'primary'}
            sx={{ height: 8, borderRadius: 4, mb: 3 }}
          />
        </Box>

        {/* Steps Navigation */}
        <Box sx={{ overflowX: 'auto' }}>
          <Stepper 
            activeStep={activeTab} 
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ 
              '& .MuiStepLabel-root': {
                cursor: 'pointer',
              },
              '& .MuiStepLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
              },
            }}
          >
            {TABS.map((tab, index) => (
              <Step key={index} completed={isStepCompleted(index)}>
                <StepLabel
                  onClick={() => setActiveTab(index)}
                  StepIconComponent={({ completed, active }) => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: completed 
                          ? 'success.main' 
                          : active 
                            ? 'primary.main' 
                            : 'grey.300',
                        color: completed || active ? 'white' : 'grey.600',
                        cursor: 'pointer',
                      }}
                    >
                      {completed ? (
                        <CheckCircleIcon sx={{ fontSize: 20, color: 'white' }} />
                      ) : (
                        <Typography variant="body2" fontWeight="bold">
                          {index + 1}
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tab.label}
                    {isStepCompleted(index) && (
                      <CheckCircleIcon 
                        sx={{ 
                          fontSize: 16, 
                          color: 'success.main',
                          ml: 0.5 
                        }} 
                      />
                    )}
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Paper>

      {/* Main Content */}
      <Grid container>
        <Grid item xs={12}>
          {/* Tab Content */}
          <Paper sx={{ mb: 3 }}>

            {/* Tab Panels */}
            <TabPanel value={activeTab} index={0}>
              <BasicInfoTab
                formData={formData}
                errors={errors}
                onFormDataChange={handleFormDataChange}
                onErrorsChange={handleErrorsChange}
                clearErrors={clearErrors}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <LocationTab
                formData={formData}
                errors={errors}
                onFormDataChange={handleFormDataChange}
                onErrorsChange={handleErrorsChange}
                clearErrors={clearErrors}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <CapacityTab
                formData={formData}
                errors={errors}
                onFormDataChange={handleFormDataChange}
                onErrorsChange={handleErrorsChange}
                clearErrors={clearErrors}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <AmenitiesTab
                formData={formData}
                errors={errors}
                onFormDataChange={handleFormDataChange}
                onErrorsChange={handleErrorsChange}
                clearErrors={clearErrors}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <PricingTab
                formData={formData}
                errors={errors}
                onFormDataChange={handleFormDataChange}
                onErrorsChange={handleErrorsChange}
                clearErrors={clearErrors}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <RulesPoliciesTab
                formData={formData}
                errors={errors}
                onFormDataChange={handleFormDataChange}
                onErrorsChange={handleErrorsChange}
                clearErrors={clearErrors}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={6}>
              <PhotosTab
                formData={formData}
                errors={errors}
                onFormDataChange={handleFormDataChange}
                onErrorsChange={handleErrorsChange}
                clearErrors={clearErrors}
                listing={listing}
              />
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Control Buttons - Always visible outside wizard */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Listing Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Save your progress or publish when ready
            </Typography>
          </Box>
          
          {/* Step Navigation */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
              disabled={activeTab === 0}
              size="small"
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setActiveTab(Math.min(TABS.length - 1, activeTab + 1))}
              disabled={activeTab === TABS.length - 1}
              size="small"
            >
              Next
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={isSubmitting || isLoading}
          >
            Save Draft
          </Button>
          
          {listing && onPublish && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PublishIcon />}
              onClick={handlePublish}
              disabled={!displayCompletionStatus.isComplete || isSubmitting || isLoading || listing.status === 'published'}
            >
              {listing.status === 'published' ? 'Published' : 'Publish Listing'}
            </Button>
          )}
          
          {listing && onPause && listing.status === 'published' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<PauseIcon />}
              onClick={handlePauseToggle}
              disabled={isSubmitting || isLoading}
            >
              Pause Listing
            </Button>
          )}
        </Box>
        
        {!displayCompletionStatus.isComplete && (
          <Alert severity="warning">
            Complete all required sections to publish your listing.
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default ListingFormTabs;